/**
 * AgentAccessPanel — owner-gated control surface for the external-agent access
 * broker. Lists every agent that has requested access (live from the
 * server-only `agentGrants` store), and lets the owner approve a time window +
 * scope, renew, edit, rename or revoke. Shows the connect code an agent needs to
 * request access in the first place. Admin-only: renders nothing for non-admins.
 *
 * Two ways in. RECOMMENDED: the owner creates the agent here (createAgentGrant)
 * and pastes a one-time `claude mcp add` command into their own terminal — the
 * agent never handles a code or secret, which is what agents' own safety checks
 * (e.g. Claude Code auto mode) block as exfiltration. FALLBACK: the agent POSTs
 * the connect code to /requestAgentAccess itself. Either way the grant starts
 * pending; approval is always a deliberate human action with an explicit
 * expiry, after which the agent must be re-approved.
 *
 * Editing (2026-10): EVERY card — pending, live, expired or revoked — has an
 * editor, and a rename (✎ or double-click the name). Edits are STAGED, not
 * fired per click: the owner changes client / Google Ads / capabilities / hosting
 * scope, sees exactly what will change in the "unsaved" bar (derived from the
 * very patch that Save sends — see agentAccessHelpers.buildUpdatePatch), then
 * saves once via the broker's `update` action, which never touches status or
 * the time window. Picking a client shows that customer's linked Google Ads
 * account straight away, because that link is what the server turns into the
 * agent's Ads allowlist (applyBoundClient).
 *
 * Layout: this panel is the sole content of a dedicated fullscreen modal
 * (AgentAccessModal), so it opens fully expanded — a two-column console with
 * "new agent" on the left and the searchable, status-grouped agent list on the
 * right. Cards are compact summaries (client · Ads · hosting · expiry · a row of
 * capability tags) that expand into the editor on demand.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAuth, onIdTokenChanged } from 'firebase/auth';
import { collection, getDocs, query as fsQuery, limit as fsLimit } from 'firebase/firestore';
import { db } from '../../../firebase';
import { toast } from 'react-toastify';
import { CF_BASE, getAgentAccessConfig, listAgentGrants, rotateAgentConnectCode, decideAgentAccess, createAgentGrant } from './googleAdsApi';
import { ClientPicker, swallowEscape } from './ClientPicker';
import {
  ClientOpt, DescribeCtx, GrantDraft,
  buildUpdatePatch, describePatch, digitsOnly, effectiveAdsAccounts, formatAdsId, parseIdList, sameSet,
} from './agentAccessHelpers';
// Portable copy of the shared design-system classes the panel uses, so it
// renders correctly when mounted outside the Google Ads page (e.g. the header modal).
import './AgentAccessPanel.scss';

interface Grant {
  id: string;
  label?: string; purpose?: string;
  status?: string; scope?: string; requestedScope?: string;
  allowedCustomerIds?: string[];
  allowedHostingIds?: string[];
  hostingAllAccounts?: boolean;
  /** Owner-set legacy flag: an UNBOUND grant with an empty account list means
   *  "all accounts" only when this is true (otherwise it means none). */
  legacyAllAccounts?: boolean;
  capabilities?: string[];
  boundCustomerId?: string;
  expiresAt?: number | null; createdAtMs?: number; lastSeenAt?: number;
  usageCount?: number; userAgent?: string;
  /** 'owner' = created in this panel (secret shown once here); 'request' = the
   *  agent requested access itself with the connect code. */
  createdVia?: 'owner' | 'request';
}

const SCOPE_LABEL: Record<string, string> = { none: 'no Google Ads', read: 'read-only', read_propose: 'read + propose' };
// Business-data capabilities beyond Google Ads (the grant's `capabilities[]`).
// Google Ads access stays on `scope`; these gate the CRM / billing / email MCP
// tools. `crm:write` is queued-only — every write awaits owner approval.
// Single-client model: every capability is scoped to the grant's bound client.
const CAP_OPTIONS: { id: string; label: string; hint: string; risk?: boolean }[] = [
  { id: 'crm:read', label: 'CRM read', hint: 'the client’s record, summary, revenue rollup' },
  { id: 'jobs:read', label: 'Jobs read', hint: 'the client’s jobs + tasks' },
  { id: 'billing:read', label: 'Invoices read', hint: 'the client’s invoices (list / detail / overdue)' },
  { id: 'email:read', label: 'Email headers', hint: 'the client’s email headers only — no bodies' },
  { id: 'email:bodies', label: '⚠ Email bodies', hint: 'HIGH RISK · full email content + search. PII exposure.', risk: true },
  { id: 'web:fetch', label: '⚠ Web fetch', hint: 'HIGH RISK · agent fetches arbitrary URLs (data-egress vector).', risk: true },
  { id: 'crm:write', label: 'Queued writes', hint: 'agent proposes changes — you approve each one', },
  // Synergy cPanel hosting — gated by the allowedHostingIds allowlist below, NOT
  // the bound client. hosting:login mints a live cPanel SSO URL (full site access).
  { id: 'hosting:read', label: 'Hosting read', hint: 'list/detail of the allowed cPanel accounts' },
  { id: 'hosting:login', label: '⚠ cPanel login', hint: 'HIGH RISK · mints a cPanel single-sign-on URL — full access to that site.', risk: true },
  { id: 'hosting.ssh', label: '⚠ SSH access', hint: 'HIGH RISK · shares the RAW SSH key/host for the BOUND client’s server (connect directly, no approval). Needs the SSH kill switch armed.', risk: true },
];
// Capabilities grouped into tiers so the grid reads as "safe reads / high-risk / writes / hosting".
const CAP_TIERS: { label: string; ids: string[] }[] = [
  { label: 'reads', ids: ['crm:read', 'jobs:read', 'billing:read', 'email:read'] },
  { label: 'high-risk', ids: ['email:bodies', 'web:fetch'] },
  { label: 'writes', ids: ['crm:write'] },
  { label: 'hosting', ids: ['hosting:read', 'hosting:login', 'hosting.ssh'] },
];
const TTL_OPTIONS = [{ h: 1, label: '1 hour' }, { h: 8, label: '8 hours' }, { h: 24, label: '24 hours' }, { h: 168, label: '7 days' }];

function fmtCountdown(exp?: number | null): string {
  if (!exp) return '—';
  const ms = exp - Date.now();
  if (ms <= 0) return 'expired';
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h left`;
  if (h >= 1) return `${h}h ${m}m left`;
  return `${m}m left`;
}
function fmtAgo(ts?: number): string {
  if (!ts) return 'never';
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}
// Colours are CSS-var driven (with the original dark hex as fallback) so a
// theme scope — e.g. html[data-app-theme='graphite'] .aap — can repaint the
// inline-styled bits (status dots, help modal, code blocks) that CSS classes
// can't reach. Non-graphite themes fall back to the literals below unchanged.
const STATUS_TONE: Record<string, string> = {
  pending: 'var(--aap-c-pending, #d6a85f)', approved: 'var(--aap-c-approved, #7ed4a0)',
  revoked: 'var(--aap-c-revoked, #c98a8a)', expired: 'var(--aap-c-expired, #9a9a9a)',
};
function liveStatus(g: Grant): string {
  if (g.status === 'approved' && g.expiresAt && Date.now() >= g.expiresAt) return 'expired';
  return String(g.status || 'pending');
}

// Help-modal style tokens. Colours are CSS-var driven (dark hex fallback) so a
// theme scope can restyle the modal without touching this JSX.
const helpStep: React.CSSProperties = { color: 'var(--aap-c-accent, #8bbf8b)', fontWeight: 600, marginTop: 14, marginBottom: 4 };
const helpP: React.CSSProperties = { color: 'var(--aap-c-dim, #9a9a9a)', margin: '0 0 8px' };
const ic: React.CSSProperties = { background: 'var(--aap-c-chip-bg, #1a1a1a)', padding: '1px 5px', borderRadius: 3, color: 'var(--aap-c-chip-fg, #cfcfcf)' };
const kvRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0', flexWrap: 'wrap' };
const kvKey: React.CSSProperties = { color: 'var(--aap-c-key, #6a6a6a)', width: 92, fontSize: 11 };
const codeInline: React.CSSProperties = { background: 'var(--aap-c-chip-bg, #1a1a1a)', padding: '2px 8px', borderRadius: 4, color: 'var(--aap-c-code-fg, #d4d4d4)', flex: '1 1 auto', overflowWrap: 'anywhere' };
const codeWrap: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'flex-start', margin: '4px 0 10px' };
const codePre: React.CSSProperties = { background: 'var(--aap-c-pre-bg, #0f0f0f)', border: '1px solid var(--aap-c-pre-border, #222)', padding: '8px 10px', borderRadius: 4, color: 'var(--aap-c-code-fg, #cfcfcf)', whiteSpace: 'pre-wrap', fontSize: 11, flex: '1 1 auto', overflowWrap: 'anywhere' };

const capLabel = (id: string): string => CAP_OPTIONS.find((c) => c.id === id)?.label ?? id;

/** In-place rename. Enter or blur commits, Esc cancels (and is swallowed so it
 *  doesn't also close the console). Declared at module level on purpose: a
 *  component defined inside the panel's render would remount — and drop focus —
 *  on every re-render. */
const RenameField: React.FC<{ initial: string; onCommit: (v: string) => void; onCancel: () => void }> = ({ initial, onCommit, onCancel }) => {
  const [v, setV] = useState(initial);
  const done = useRef(false);   // Enter then blur must not commit twice
  const finish = (commit: boolean) => {
    if (done.current) return;
    done.current = true;
    if (commit) onCommit(v); else onCancel();
  };
  return (
    <input
      className="aap__rename gads-bar__picker"
      type="text"
      autoFocus
      maxLength={80}
      aria-label="Agent name"
      value={v}
      onFocus={(e) => e.target.select()}
      onChange={(e) => setV(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); finish(true); }
        else if (e.key === 'Escape') { e.preventDefault(); swallowEscape(e); finish(false); }
      }}
      onBlur={() => finish(true)}
    />
  );
};

export const AgentAccessPanel: React.FC = () => {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [grants, setGrants] = useState<Grant[]>([]);
  const [connectCode, setConnectCode] = useState<string>('');
  const [showHelp, setShowHelp] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [busy, setBusy] = useState<string>('');
  const [clients, setClients] = useState<ClientOpt[]>([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  // Staged, unsaved edits per card. Holds ONLY the fields the owner has touched
  // (see GrantDraft) — never materialised defaults — so "dirty" is exactly
  // "differs from what's saved".
  const [draft, setDraft] = useState<Record<string, GrantDraft>>({});
  // Editor open/closed per card; unset = pending cards open, the rest collapsed.
  const [editorOpen, setEditorOpen] = useState<Record<string, boolean>>({});
  const [renaming, setRenaming] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [, forceTick] = useState(0);
  // Owner-created agent: the one-time secret lives ONLY in this component's
  // memory (the server keeps just its hash) — closing the panel discards it.
  const [minted, setMinted] = useState<{ id: string; secret: string } | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [newClient, setNewClient] = useState('');
  const [creating, setCreating] = useState(false);

  // ── Owner gate ─────────────────────────────────────────────────────
  // Gate on the ACTUAL owner authority — getAgentAccessConfig is requireOwner —
  // rather than a Firebase custom claim (which this app doesn't reliably set).
  // Driven by the auth listener so it runs only once a user/token exists
  // (authFetch throws without one), making it robust to a hard refresh straight
  // onto this page. Success ⇒ you're the owner ⇒ show the panel + connect code.
  useEffect(() => {
    let cancelled = false;
    const unsub = onIdTokenChanged(getAuth(), async (u) => {
      if (!u) { if (!cancelled) setAllowed(false); return; }
      const r = await getAgentAccessConfig();
      if (cancelled) return;
      if (r.ok) { setAllowed(true); if (r.connectCode) setConnectCode(r.connectCode); }
      else setAllowed(false);
    });
    return () => { cancelled = true; unsub(); };
  }, []);

  // ── Grants list (polled through the owner endpoint, NOT a Firestore client
  // read — the agentGrants rule gates on users.role=='admin' which the owner may
  // not carry, so a direct read would silently come back empty). ──────────────
  const refresh = useCallback(async () => {
    const r = await listAgentGrants();
    if (r.ok && r.grants) setGrants(r.grants as Grant[]);
  }, []);
  useEffect(() => {
    if (allowed !== true) return;
    let stop = false;
    refresh();
    const t = setInterval(() => { if (!stop) refresh(); }, 7000);
    return () => { stop = true; clearInterval(t); };
  }, [allowed, refresh]);

  // Customers for the client picker (loaded once for the owner). Deliberately
  // NOT ordered by name server-side: Firestore drops documents that lack the
  // order-by field, and a customer with a blank name must still be pickable.
  // Each carries its linked Google Ads account so the picker and the cards can
  // show what binding an agent to that client actually grants.
  useEffect(() => {
    if (allowed !== true) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(fsQuery(collection(db, 'customers'), fsLimit(2000)));
        if (cancelled) return;
        setClients(snap.docs.map((d) => {
          const x = d.data() as any;
          const company = String(x?.company || '').trim();
          return {
            id: d.id,
            name: String(x?.name || '').trim() || company || d.id,
            company: company || undefined,
            email: String(x?.email || '').trim() || undefined,
            adsId: digitsOnly(x?.googleAdsCustomerId) || undefined,
            adsName: String(x?.googleAdsCustomerName || '').trim() || undefined,
          };
        }));
      } catch { /* rules/permission — leave empty, the agents list still works */ }
      finally { if (!cancelled) setClientsLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [allowed]);

  const clientById = useMemo(() => new Map(clients.map((c) => [c.id, c] as const)), [clients]);
  const clientName = useCallback((id?: string) => (id ? (clientById.get(id)?.name ?? id) : ''), [clientById]);
  const linkedAdsOf = useCallback((id: string) => clientById.get(id)?.adsId || '', [clientById]);
  const adsNames = useMemo(() => {
    const m: Record<string, string> = {};
    for (const c of clients) if (c.adsId && c.adsName) m[c.adsId] = c.adsName;
    return m;
  }, [clients]);
  const adsLabel = useCallback(
    (cid: string) => (adsNames[cid] ? `${adsNames[cid]} · ${formatAdsId(cid)}` : formatAdsId(cid)),
    [adsNames],
  );
  const describeCtx = useMemo<DescribeCtx>(
    () => ({ clientName, capLabel, scopeLabel: (s) => SCOPE_LABEL[s] || s, adsLabel }),
    [clientName, adsLabel],
  );

  // Re-render every 30s so countdowns stay fresh (the console is always expanded).
  useEffect(() => {
    const t = setInterval(() => forceTick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);

  // ── Search + grouping ──────────────────────────────────────────────
  const matches = useCallback((g: Grant) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const hay = [g.label, g.purpose, g.id, clientName(g.boundCustomerId), ...(g.allowedCustomerIds || []).map(adsLabel)]
      .join(' ').toLowerCase();
    return q.split(/\s+/).every((t) => hay.includes(t));
  }, [search, clientName, adsLabel]);
  const pending = useMemo(() => grants.filter((g) => liveStatus(g) === 'pending' && matches(g)), [grants, matches]);
  const active = useMemo(() => grants.filter((g) => liveStatus(g) === 'approved' && matches(g)), [grants, matches]);
  const inactive = useMemo(() => grants.filter((g) => { const s = liveStatus(g); return (s === 'expired' || s === 'revoked') && matches(g); }), [grants, matches]);
  const totals = useMemo(() => {
    let p = 0, a = 0, i = 0;
    for (const g of grants) {
      const s = liveStatus(g);
      if (s === 'pending') p++; else if (s === 'approved') a++; else if (s === 'expired' || s === 'revoked') i++;
    }
    return { pending: p, active: a, inactive: i };
  }, [grants]);
  const searching = search.trim() !== '';
  const inactiveOpen = showInactive || searching;

  // ── Staged edits ───────────────────────────────────────────────────
  // What a card SHOWS = the saved grant overlaid with the owner's staged edits.
  // Pending requests keep the historic 'read + propose' starting point for the
  // Google Ads select; every other grant starts at its own saved scope — an
  // approved grant must never display (and then renew with) a scope it doesn't have.
  const scopeDefault = (g: Grant): string =>
    (liveStatus(g) === 'pending' && g.createdVia !== 'owner' ? 'read_propose' : (g.scope || 'none'));
  const draftFor = (g: Grant) => {
    const d = draft[g.id] || {};
    return {
      ttl: d.ttl ?? 24,
      scope: d.scope ?? scopeDefault(g),
      caps: d.caps ?? (g.capabilities || []),
      client: d.client ?? (g.boundCustomerId || ''),
      hostingAll: d.hostingAll ?? (g.hostingAllAccounts === true),
    };
  };
  const setDraftFor = (g: Grant, patch: Partial<GrantDraft>) =>
    setDraft((prev) => ({ ...prev, [g.id]: { ...prev[g.id], ...patch } }));
  const clearDraft = (id: string, keepTtl = false) =>
    setDraft((prev) => {
      const next = { ...prev };
      const ttl = prev[id]?.ttl;
      delete next[id];
      if (keepTtl && ttl !== undefined) next[id] = { ttl };
      return next;
    });
  const toggleCap = (g: Grant, cap: string) => {
    const cur = draftFor(g).caps;
    setDraftFor(g, { caps: cur.includes(cap) ? cur.filter((c) => c !== cap) : [...cur, cap] });
  };
  // Picking a different client re-derives the Ads allowlist server-side, so any
  // half-typed account override from the previous client is dropped with it.
  const setClient = (g: Grant, id: string) => setDraftFor(g, { client: id, accounts: undefined });

  // ── Actions ────────────────────────────────────────────────────────
  // Persist a card's staged edits via the broker's `update` action: changes only
  // what differs, never touches status or the time window, effective on the
  // agent's very next call. One request, one confirmation.
  const saveEdits = async (g: Grant) => {
    const patch = buildUpdatePatch(g, draft[g.id], linkedAdsOf);
    if (Object.keys(patch).length === 0) return;
    setBusy(g.id);
    const r = await decideAgentAccess(g.id, 'update', patch);
    if (!r.ok) { setBusy(''); toast.error(r.error || 'could not save changes'); return; }
    await refresh();            // show the saved values BEFORE dropping the draft, so the card never flashes the old ones
    clearDraft(g.id, true);
    setBusy('');
    toast.success(liveStatus(g) === 'approved' ? 'saved — live on the agent’s next call' : 'changes saved');
  };

  const commitRename = async (g: Grant, raw: string) => {
    setRenaming(null);
    const next = raw.trim();
    if (!next || next === (g.label || '')) return;      // empty = cancel; the server rejects blanks too
    setGrants((gs) => gs.map((x) => (x.id === g.id ? { ...x, label: next } : x)));   // optimistic
    setBusy(g.id);
    const r = await decideAgentAccess(g.id, 'update', { label: next });
    setBusy('');
    if (r.ok) toast.success('renamed'); else toast.error(r.error || 'rename failed');
    await refresh();            // authoritative either way (rolls the optimistic label back on failure)
  };

  // Re-point an approved grant's Google Ads access at its client's CURRENT linked
  // account (a no-change update that carries the same boundCustomerId, which the
  // server turns into a fresh derivation).
  const syncAds = async (g: Grant) => {
    if (!g.boundCustomerId) return;
    setBusy(g.id);
    const r = await decideAgentAccess(g.id, 'update', { boundCustomerId: g.boundCustomerId });
    setBusy('');
    if (!r.ok) { toast.error(r.error || 'sync failed'); return; }
    toast.success('Google Ads access now matches the customer’s linked account');
    await refresh();
  };

  const act = async (g: Grant, action: 'approve' | 'extend' | 'revoke' | 'delete') => {
    if (action === 'revoke' && !window.confirm(`Revoke access for “${g.label || g.id}”? It stops working immediately.`)) return;
    if (action === 'delete' && !window.confirm(`Delete “${g.label || g.id}” from the list?`)) return;
    setBusy(g.id);
    const ef = draftFor(g);
    const rawAccounts = draft[g.id]?.accounts;
    let opts: { ttlHours: number; scope: 'read' | 'read_propose' | 'none'; allowedCustomerIds?: string[]; capabilities?: string[]; boundCustomerId?: string; allowedHostingIds?: string[]; hostingAllAccounts?: boolean } | undefined;
    if (action === 'approve' || action === 'extend') {
      // Read through draftFor so this sends exactly what the card SHOWS.
      opts = { ttlHours: ef.ttl, scope: ef.scope as 'read' | 'read_propose' | 'none' };
      // Only a grant awaiting its first approval re-derives its Ads allowlist from
      // the customer's current link. Renewing / re-approving leaves the saved list
      // alone unless the owner changed the client or typed an override: the server
      // re-derives whenever boundCustomerId is present, which would silently
      // overwrite a deliberate account list on every renewal.
      const rederive = liveStatus(g) === 'pending';
      if (rawAccounts !== undefined) {
        const list = parseIdList(rawAccounts, { digits: true });
        // A blank override on a bound grant means follow the customer's account.
        if (list.length || !ef.client) opts.allowedCustomerIds = list;
      } else if (!ef.client && rederive) {
        opts.allowedCustomerIds = g.allowedCustomerIds || [];   // nothing to derive from, so keep what is saved
      }
      if (rederive || ef.client !== (g.boundCustomerId || '')) opts.boundCustomerId = ef.client;
      // Always send capabilities on approve/extend: the draft falls back to the
      // grant's existing set, so re-approving without touching the boxes preserves it.
      opts.capabilities = ef.caps;
      // Synergy hosting scope (allowlist is alphanumeric hoids, so do NOT digit-strip).
      // hostids falls back to the grant's existing list so re-approve preserves it.
      const hostids = draft[g.id]?.hostids;
      opts.allowedHostingIds = parseIdList(hostids !== undefined ? hostids : (g.allowedHostingIds || []).join(','));
      opts.hostingAllAccounts = ef.hostingAll;
    }
    const r = await decideAgentAccess(g.id, action, opts);
    if (!r.ok) { setBusy(''); toast.error(r.error || 'failed'); return; }
    await refresh();
    clearDraft(g.id, true);
    setBusy('');
    toast.success(`agent ${action === 'approve' ? 'approved' : action === 'extend' ? 'renewed' : action === 'revoke' ? 'revoked' : 'deleted'}`);
  };

  const copy = (s: string) => { try { navigator.clipboard?.writeText(s); toast.success('copied'); } catch { /* clipboard blocked */ } };
  const rotate = async () => {
    if (!window.confirm('Rotate the connect code? Any agent that hasn’t connected yet will need the new code.')) return;
    const r = await rotateAgentConnectCode();
    if (r.ok && r.connectCode) { setConnectCode(r.connectCode); toast.success('connect code rotated'); }
    else toast.error(r.error || 'failed');
  };

  // Owner-created agent → a pending card carrying its one-time setup command.
  const createAgent = async () => {
    setCreating(true);
    const label = newLabel.trim() || (newClient ? `Claude Code – ${clientName(newClient)}` : 'Claude Code agent');
    const r = await createAgentGrant(label, newClient || undefined);
    setCreating(false);
    if (!r.ok || !r.agentUid || !r.agentSecret) { toast.error(r.error || 'could not create agent'); return; }
    setMinted({ id: r.agentUid, secret: r.agentSecret });
    setNewLabel(''); setNewClient('');
    await refresh();
    // Bring the new card (and its shown-once command) into view.
    const id = r.agentUid;
    setTimeout(() => document.getElementById(`aap-grant-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  };

  const manifestUrl = `${CF_BASE}/agentAccessManifest`;
  const mcpUrl = `${CF_BASE}/agentMcp`;
  // The request endpoint is stated IN the kit (not left for the agent to lift
  // from the fetched manifest), so the destination of the connect code comes
  // from the owner's own message.
  const onboardingKit = `Web Construct — agent access
1) GET this manifest first — it lists every endpoint, the auth scheme, and the payload schema:
   ${manifestUrl}
2) Request access by POSTing { connectCode, label, purpose, idempotencyKey } to:
   ${CF_BASE}/requestAgentAccess
   Connect code: ${connectCode}

You receive { agentUid, agentSecret } (store the secret — it's shown once; reuse the same idempotencyKey on retries). You'll appear in the owner's panel as "pending"; once approved you get scoped, single-client access (Google Ads + whichever business-data capabilities the owner grants). Every change is owner-approved — nothing applies automatically. You never receive our Google Ads credentials; the only raw credential the broker can hand over is a client's SiteGround SSH key, and only if the owner grants SSH access.

MCP-native clients (e.g. Claude Code): once approved, add a remote MCP server and your tools are auto-discovered:
   URL: ${mcpUrl}   ·   Header: Authorization: Bearer <your agentSecret>`;
  const mcpAddCmd = `claude mcp add --transport http web-construct ${mcpUrl} --header "Authorization: Bearer <agentSecret>"`;
  // .mcp.json is shared with the repo, so the secret comes from an env var —
  // never inline it there.
  const mcpJson = `{
  "mcpServers": {
    "web-construct": {
      "type": "http",
      "url": "${mcpUrl}",
      "headers": { "Authorization": "Bearer \${WEB_CONSTRUCT_AGENT_SECRET}" }
    }
  }
}`;
  const mintedCmd = minted
    ? `claude mcp add --transport http web-construct ${mcpUrl} --header "Authorization: Bearer ${minted.secret}"`
    : '';

  if (allowed !== true) return null;

  // ── One agent card ─────────────────────────────────────────────────
  // Collapsed: name · status · client · Google Ads · expiry · capability tags.
  // Expanded (pending cards start expanded): the editor + staged-changes bar.
  type Tone = '' | 'ok' | 'warn';

  // The Google Ads chip tells the truth about what the agent can reach. A bound
  // grant with an empty allowlist reaches NO Ads account (the server fails closed),
  // so it must never read as "all accounts".
  const adsFact = (g: Grant, st: string): { text: string; tone: Tone; title: string } => {
    const scope = g.scope || 'read';
    if (scope === 'none') return { text: 'Google Ads off', tone: '', title: 'This agent has no Google Ads access.' };
    const level = SCOPE_LABEL[scope] || scope;
    // Until the customer list has loaded a link can't be resolved, so show what is saved.
    const accts = clientsLoaded
      ? effectiveAdsAccounts(g, undefined, linkedAdsOf, st === 'pending')
      : (g.allowedCustomerIds || []);
    if (accts.length) {
      const shown = accts.slice(0, 2).map(adsLabel).join(', ') + (accts.length > 2 ? ` +${accts.length - 2}` : '');
      return { text: `Ads ${level} · ${shown}`, tone: 'ok', title: accts.map(adsLabel).join('\n') };
    }
    if (g.boundCustomerId) {
      return {
        text: `Ads ${level} · no account linked`, tone: 'warn',
        title: `${clientName(g.boundCustomerId)} has no Google Ads account linked, so this agent cannot reach Google Ads. Link one on the customer’s Ads tab, then match it here.`,
      };
    }
    if (g.legacyAllAccounts) {
      return { text: `Ads ${level} · all accounts (legacy)`, tone: 'warn', title: 'Legacy grant with no bound client: it reaches every account under the MCC.' };
    }
    return { text: `Ads ${level} · no account`, tone: 'warn', title: 'No client is bound and no account list is set, so Google Ads is unreachable.' };
  };

  const timeFact = (g: Grant, st: string): { text: string; tone: Tone } => {
    if (st === 'approved') {
      const c = fmtCountdown(g.expiresAt);
      return { text: c.endsWith(' left') ? `expires in ${c.slice(0, -5)}` : c, tone: g.expiresAt && g.expiresAt - Date.now() < 3600000 ? 'warn' : '' };
    }
    if (st === 'expired') return { text: `expired ${fmtAgo(g.expiresAt || undefined)}`, tone: '' };
    if (st === 'revoked') return { text: 'revoked', tone: '' };
    return { text: 'awaiting approval', tone: 'warn' };
  };

  const renderGrant = (g: Grant) => {
    const st = liveStatus(g);
    const isBusy = busy === g.id;
    const ef = draftFor(g);
    const patch = buildUpdatePatch(g, draft[g.id], linkedAdsOf);
    const changes = describePatch(g, patch, describeCtx);
    const dirty = Object.keys(patch).length > 0;
    const isEditing = editorOpen[g.id] ?? (st === 'pending');
    // Block approval of a Google Ads grant with no bound client: an unbound Ads
    // grant would otherwise be a cross-client escape. The backend also rejects
    // this; disabling the button surfaces it before the click.
    const needsClient = ef.scope !== 'none' && !ef.client;
    const needsClientTitle = 'Bind a client first (or set Google Ads to “none”) — an unbound Ads grant would expose every account under the MCC.';
    const ads = adsFact(g, st);
    const time = timeFact(g, st);
    const savedCaps = g.capabilities || [];
    const capTags = [
      ...CAP_OPTIONS.filter((c) => savedCaps.includes(c.id)),
      ...savedCaps.filter((id) => !CAP_OPTIONS.some((c) => c.id === id)).map((id) => ({ id, label: id, hint: '', risk: false })),
    ];
    const hostingGranted = savedCaps.some((c) => c.startsWith('hosting:'));

    // Editor readouts ──────────────────────────────────────────────
    const savedAccts = g.allowedCustomerIds || [];
    const savedLink = g.boundCustomerId ? linkedAdsOf(g.boundCustomerId) : '';
    const accountsRaw = draft[g.id]?.accounts;
    const clientStaged = ef.client !== (g.boundCustomerId || '');
    // An allowlist that differs from the saved client's linked account is a
    // deliberate override; show it in the advanced field so it isn't invisible.
    const hasOverride = savedAccts.length > 0 && !sameSet(savedAccts, savedLink ? [savedLink] : []);
    const overrideValue = accountsRaw ?? ((hasOverride && !clientStaged) ? savedAccts.join(', ') : '');
    const effAccts = effectiveAdsAccounts(g, draft[g.id], linkedAdsOf, st === 'pending');
    const customerLink = ef.client ? linkedAdsOf(ef.client) : '';
    const drift = clientsLoaded && st !== 'pending' && !!g.boundCustomerId && (g.scope || 'read') !== 'none'
      && draft[g.id]?.client === undefined && accountsRaw === undefined
      && !sameSet(savedAccts, savedLink ? [savedLink] : []);

    let readoutTone: 'ok' | 'warn' | 'idle' = 'idle';
    let readout: React.ReactNode;
    if (!clientsLoaded) {
      readout = 'Loading customers…';
    } else if (ef.scope === 'none') {
      readout = customerLink
        ? <>Google Ads access is <b>off</b>, so the linked account ({adsLabel(customerLink)}) is not used.</>
        : 'Google Ads access is off for this agent.';
    } else if (effAccts.length) {
      readoutTone = 'ok';
      const src = digitsOnly(accountsRaw).length ? 'custom list (advanced)' : (ef.client ? 'linked on the customer record' : 'saved on this agent');
      readout = <>◆ <span className="aap__ads-acct">{effAccts.map(adsLabel).join(' · ')}</span> <span className="aap__ads-src">— {src}</span></>;
    } else if (ef.client) {
      readoutTone = 'warn';
      readout = <><b>{clientName(ef.client)}</b> has no Google Ads account linked, so this agent can’t use Google Ads for them. Link one on the customer’s Ads tab.</>;
    } else {
      readout = 'Pick a client — their linked Google Ads account is included automatically.';
    }

    return (
      <article key={g.id} id={`aap-grant-${g.id}`} className={`aap__grant aap__grant--${st}${isEditing ? ' is-editing' : ''}`}>
        <div className="aap__grant-head">
          <div className="aap__grant-titlebox">
            {renaming === g.id ? (
              <RenameField initial={g.label || ''} onCommit={(v) => commitRename(g, v)} onCancel={() => setRenaming(null)} />
            ) : (
              <>
                <span className="aap__grant-label" title="Double-click to rename" onDoubleClick={() => setRenaming(g.id)}>{g.label || '(unnamed)'}</span>
                <button type="button" className="aap__icon-btn" aria-label={`Rename ${g.label || 'agent'}`} title="Rename" disabled={isBusy} onClick={() => setRenaming(g.id)}>✎</button>
              </>
            )}
            <span className={`aap__pill aap__pill--${st}`}>{st}</span>
          </div>
          <span className="aap__grant-usage">{g.lastSeenAt ? `seen ${fmtAgo(g.lastSeenAt)}` : 'never connected'} · {g.usageCount || 0} calls</span>
        </div>
        {g.purpose && <div className="aap__grant-purpose">{g.purpose}</div>}

        {/* One-time setup command for an owner-created agent. The secret exists
            only in this component's memory — dismissing it (or closing the
            panel) discards it for good. */}
        {minted?.id === g.id && (
          <div style={{ border: '1px solid var(--aap-c-ok-border, #1f5a33)', borderRadius: 6, padding: '10px 12px', margin: '8px 0 10px', background: 'var(--aap-c-pre-bg, #0f0f0f)' }}>
            <div style={{ ...helpStep, marginTop: 0 }}>setup command · shown once</div>
            <div style={codeWrap}><code style={codePre}>{mintedCmd}</code><button type="button" className="gads-deep__act-btn" onClick={() => copy(mintedCmd)}>copy</button></div>
            <ol style={{ ...helpP, paddingLeft: 18, margin: '4px 0 8px' }}>
              <li>Tick what this agent needs below, then <b>approve</b>.</li>
              <li>Run the command in your terminal, from the agent’s project folder. (Added one there before? Run <code style={ic}>claude mcp remove web-construct</code> first.)</li>
              <li>Start Claude Code in that folder and give it the task — it calls <code style={ic}>access_status</code> first and takes it from there. Already open? Restart it so it picks up the tools.</li>
            </ol>
            <div style={{ ...helpP, marginBottom: 6 }}>Only a hash of this secret is kept — if you lose it, delete this agent and create another.</div>
            <button type="button" className="gads-deep__act-btn" onClick={() => setMinted(null)}>done — I’ve copied it</button>
          </div>
        )}

        <div className="aap__facts">
          <span className={`aap__fact aap__fact--${g.boundCustomerId ? 'ok' : 'warn'}`} title={g.boundCustomerId ? `bound client: ${g.boundCustomerId}` : 'No client is bound, so this agent cannot reach any client data.'}>
            ◆ {g.boundCustomerId ? clientName(g.boundCustomerId) : 'no client bound'}
          </span>
          <span className={`aap__fact${ads.tone ? ` aap__fact--${ads.tone}` : ''}`} title={ads.title}>{ads.text}</span>
          {hostingGranted && (
            <span className="aap__fact" title={g.hostingAllAccounts ? 'all hosting accounts' : (g.allowedHostingIds || []).join(', ')}>
              {g.hostingAllAccounts ? 'all hosting' : `${(g.allowedHostingIds || []).length} hosting`}
            </span>
          )}
          <span className={`aap__fact aap__fact--time${time.tone ? ` aap__fact--${time.tone}` : ''}`}>{time.text}</span>
        </div>
        <div className="aap__tags">
          {capTags.length === 0
            ? <span className="aap__tag aap__tag--none">no business data</span>
            : capTags.map((c) => <span key={c.id} className={`aap__tag${c.risk ? ' aap__tag--risk' : ''}`} title={c.hint}>{c.label}</span>)}
        </div>

        {isEditing && (
          <div className="aap__editor">
            <section className="aap__sec">
              <div className="aap__sec-title">client &amp; Google Ads</div>
              <div className="aap__control">
                <label className="aap__control-label" htmlFor={`aap-client-${g.id}`}>bound client</label>
                <ClientPicker
                  inputId={`aap-client-${g.id}`}
                  clients={clients}
                  value={ef.client}
                  loading={!clientsLoaded}
                  disabled={isBusy}
                  tone={ef.client ? 'ok' : 'warn'}
                  ariaLabel="Bound client"
                  placeholder="search clients — or none (no business data)"
                  title="The ONE client this agent is bound to. Every business tool is scoped to this client; its linked Google Ads account is included automatically."
                  onChange={(id) => setClient(g, id)}
                />
              </div>
              <div className="aap__control">
                <label className="aap__control-label" htmlFor={`aap-scope-${g.id}`}>Google Ads access</label>
                <select id={`aap-scope-${g.id}`} className="gads-bar__picker" value={ef.scope} disabled={isBusy}
                  onChange={(e) => setDraftFor(g, { scope: e.target.value })}>
                  <option value="none">none</option>
                  <option value="read">read-only</option>
                  <option value="read_propose">read + propose</option>
                </select>
              </div>
              <div className={`aap__ads aap__ads--${readoutTone}`} role="status">{readout}</div>
              {drift && (
                <div className="aap__sync">
                  <span>
                    Out of sync — this agent can reach {savedAccts.length ? savedAccts.map(adsLabel).join(', ') : 'no Ads account'},
                    but <b>{clientName(g.boundCustomerId)}</b> is {savedLink ? `linked to ${adsLabel(savedLink)}` : 'not linked to any Ads account'}.
                  </span>
                  <button type="button" className="aap__btn" disabled={isBusy} onClick={() => syncAds(g)}>match the customer</button>
                </div>
              )}
              <details className="aap__adv" open={hasOverride || accountsRaw !== undefined}>
                <summary>advanced · restrict to specific Ads account IDs</summary>
                <input type="text" className="gads-bar__picker" aria-label="Google Ads account IDs override"
                  placeholder={customerLink ? `blank = ${formatAdsId(customerLink)} (the customer’s account)` : 'blank = follow the customer’s linked account'}
                  disabled={isBusy}
                  value={overrideValue}
                  onChange={(e) => setDraftFor(g, { accounts: e.target.value })} />
                <div className="aap__note">Comma-separated account IDs. Replaces the customer’s linked account for this agent only; clear the box to follow the link again.</div>
              </details>
            </section>

            <section className="aap__sec">
              <div className="aap__sec-title">business data access</div>
              {CAP_TIERS.map((tier) => (
                <div key={tier.label}>
                  <div className="aap__cap-tier">{tier.label}</div>
                  <div className="aap__cap-grid">
                    {tier.ids.map((id) => {
                      const c = CAP_OPTIONS.find((x) => x.id === id)!;
                      const on = ef.caps.includes(c.id);
                      return (
                        <label key={c.id} className={`aap__cap${c.risk ? ' aap__cap--risk' : ''}${on ? ' aap__cap--on' : ''}`}>
                          <input type="checkbox" disabled={isBusy} checked={on} onChange={() => toggleCap(g, c.id)} />
                          <span className="aap__cap-label">{c.label}</span>
                          <span className="aap__cap-hint">{c.hint}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </section>

            {/* Synergy hosting scope — gated by allowedHostingIds (NOT the bound
                client). Shown only when a hosting capability is ticked. */}
            {(ef.caps.includes('hosting:read') || ef.caps.includes('hosting:login')) && (
              <section className="aap__sec aap__sec--wide">
                <div className="aap__sec-title">hosting accounts <span className="aap__sec-sub">· which Synergy cPanel accounts this agent may touch</span></div>
                <label className={`aap__cap aap__cap--risk${ef.hostingAll ? ' aap__cap--on' : ''}`}>
                  <input type="checkbox" disabled={isBusy} checked={!!ef.hostingAll}
                    onChange={() => setDraftFor(g, { hostingAll: !ef.hostingAll })} />
                  <span className="aap__cap-label">⚠ full fleet</span>
                  <span className="aap__cap-hint">ALL hosting accounts + fleet listing — only for fully-trusted agents</span>
                </label>
                {!ef.hostingAll && (
                  <div className="aap__control">
                    <label className="aap__control-label" htmlFor={`aap-hoid-${g.id}`}>allowed hosting IDs (hoid)</label>
                    <input id={`aap-hoid-${g.id}`} type="text" className="gads-bar__picker" placeholder="blank = none — paste hoid(s)"
                      title="Which specific hosting accounts (by Synergy hoid) this agent may touch. Blank = none (fail-closed). Get hoids from Andrei: “list our hosting accounts”."
                      disabled={isBusy}
                      value={draft[g.id]?.hostids ?? (g.allowedHostingIds || []).join(', ')}
                      onChange={(e) => setDraftFor(g, { hostids: e.target.value })} />
                  </div>
                )}
              </section>
            )}
          </div>
        )}

        {dirty && (
          <div className="aap__dirty" role="status">
            <span className="aap__dirty-text">
              <b>{changes.length} unsaved {changes.length === 1 ? 'change' : 'changes'}</b> · {changes.join(' · ')}
              {st === 'pending' && ' (also applied if you approve)'}
              {st === 'approved' && ' — takes effect on the agent’s next call; the time window is untouched'}
            </span>
            <button type="button" className="aap__btn aap__btn--approve" disabled={isBusy} onClick={() => saveEdits(g)}>{isBusy ? '…' : 'save changes'}</button>
            <button type="button" className="aap__btn" disabled={isBusy} onClick={() => clearDraft(g.id, true)}>discard</button>
          </div>
        )}

        <div className="aap__grant-actions">
          <button type="button" className="aap__btn" aria-expanded={isEditing}
            onClick={() => setEditorOpen((o) => ({ ...o, [g.id]: !isEditing }))}>
            {isEditing ? 'close editor' : 'edit'}
          </button>
          <span className="aap__spacer" />
          <span className="aap__window">
            <label className="aap__window-label" htmlFor={`aap-ttl-${g.id}`}>for</label>
            <select id={`aap-ttl-${g.id}`} className="gads-bar__picker" value={ef.ttl} disabled={isBusy}
              onChange={(e) => setDraftFor(g, { ttl: Number(e.target.value) })}>
              {TTL_OPTIONS.map((o) => <option key={o.h} value={o.h}>{o.label}</option>)}
            </select>
            {st === 'pending' && <button type="button" className="aap__btn aap__btn--approve" disabled={isBusy || needsClient} title={needsClient ? needsClientTitle : 'Approve with the settings shown above'} onClick={() => act(g, 'approve')}>{isBusy ? '…' : 'approve'}</button>}
            {st === 'approved' && <button type="button" className="aap__btn" disabled={isBusy} title="Sets the access window to this length from now. It replaces the time left, it does not add to it." onClick={() => act(g, 'extend')}>{isBusy ? '…' : 'renew'}</button>}
            {(st === 'expired' || st === 'revoked') && <button type="button" className="aap__btn aap__btn--approve" disabled={isBusy || needsClient} title={needsClient ? needsClientTitle : 'Approve again with the settings shown above'} onClick={() => act(g, 'approve')}>{isBusy ? '…' : 're-approve'}</button>}
          </span>
          {st === 'pending' && <button type="button" className="aap__btn aap__btn--danger" disabled={isBusy} onClick={() => act(g, 'delete')}>deny</button>}
          {st === 'approved' && <button type="button" className="aap__btn aap__btn--danger" disabled={isBusy} onClick={() => act(g, 'revoke')}>revoke</button>}
          {(st === 'expired' || st === 'revoked') && <button type="button" className="aap__btn aap__btn--danger" disabled={isBusy} onClick={() => act(g, 'delete')}>delete</button>}
        </div>
        <div className="aap__grant-id">{g.id} · {g.createdVia === 'owner' ? 'created by you' : `requested ${SCOPE_LABEL[g.requestedScope || 'read_propose'] || g.requestedScope}`}</div>
      </article>
    );
  };

  return (
    <div className="aap">
      <div className="aap__grid">
        {/* ── left: create a new agent ──────────────────────────────── */}
        <aside className="aap__connect">
          <div className="aap__card-title">new agent <span className="aap__card-tag">· recommended</span></div>
          <div className="aap__hint">
            Create the access here, then paste one command into your terminal. The agent never
            handles a code or secret itself, so its safety checks have nothing to block.
          </div>
          <div className="aap__field">
            <label className="aap__label" htmlFor="aap-new-label">label</label>
            <input id="aap-new-label" type="text" className="gads-bar__picker" style={{ width: '100%', boxSizing: 'border-box' }} maxLength={80}
              placeholder={newClient ? `Claude Code – ${clientName(newClient)}` : 'e.g. Claude Code – site fixes'}
              value={newLabel} disabled={creating} onChange={(e) => setNewLabel(e.target.value)} />
          </div>
          <div className="aap__field">
            <label className="aap__label" htmlFor="aap-new-client">bound client</label>
            <ClientPicker
              inputId="aap-new-client"
              clients={clients}
              value={newClient}
              loading={!clientsLoaded}
              disabled={creating}
              ariaLabel="Bound client for the new agent"
              placeholder="search clients — or pick when approving"
              onChange={setNewClient}
            />
            {newClient && clientsLoaded && (
              <div className="aap__note">
                Google Ads: {linkedAdsOf(newClient) ? adsLabel(linkedAdsOf(newClient)) : 'no account linked to this customer'}
              </div>
            )}
          </div>
          <button type="button" className="aap__kit-btn" onClick={createAgent} disabled={creating}
            title="Creates a pending agent with a one-time setup command — scope + approve it on the right">
            {creating ? 'creating…' : '＋ create agent'}
          </button>

          <details className="aap__more">
            <summary>or let the agent request access</summary>
            <div className="aap__hint">
              For agents that can’t use MCP. The agent sends this code to us itself, then appears on the
              right as <b style={{ color: STATUS_TONE.pending }}>pending</b>. Some agents’ safety checks
              (e.g. Claude Code’s auto mode) block that as possible exfiltration — if so, use <b>new agent</b> above.
            </div>
            <div className="aap__field">
              <label className="aap__label">connect code</label>
              <code className="aap__code aap__code--big">{connectCode || '…'}</code>
              <div className="aap__code-row" style={{ marginTop: 6 }}>
                <button type="button" className="gads-deep__act-btn" style={{ flex: 1 }} onClick={() => copy(connectCode)} disabled={!connectCode}>copy code</button>
                <button type="button" className="gads-deep__act-btn" style={{ flex: 1 }} onClick={rotate} title="Mint a new code; the old one stops working">rotate</button>
              </div>
            </div>
            <button type="button" className="aap__kit-btn" onClick={() => copy(onboardingKit)} disabled={!connectCode}
              title="Copy a self-contained block (manifest URL + connect code + instructions) to paste to a new agent">
              📋 copy onboarding kit
            </button>
            <div className="aap__field">
              <label className="aap__label">MCP endpoint</label>
              <div className="aap__code-row"><code className="aap__code">{mcpUrl}</code><button type="button" className="gads-deep__act-btn" onClick={() => copy(mcpUrl)}>copy</button></div>
            </div>
            <div className="aap__field">
              <label className="aap__label">manifest</label>
              <div className="aap__code-row"><code className="aap__code">{manifestUrl}</code><button type="button" className="gads-deep__act-btn" onClick={() => copy(manifestUrl)}>copy</button></div>
            </div>
          </details>

          <button type="button" className="gads-deep__neg-btn aap__help-btn" onClick={() => setShowHelp(true)}>📖 full connection instructions</button>
        </aside>

        {/* ── right: agents ─────────────────────────────────────────── */}
        <main className="aap__agents">
          <div className="aap__toolbar">
            <input type="search" className="aap__search gads-bar__picker" aria-label="Search agents"
              placeholder="search agents — name, client, id…" value={search} onChange={(e) => setSearch(e.target.value)} />
            <div className="aap__counts">
              <span className="aap__count aap__count--pending">{totals.pending} pending</span>
              <span className="aap__count aap__count--active">{totals.active} active</span>
              <span className="aap__count">{totals.inactive} inactive</span>
            </div>
          </div>
          {grants.length === 0 ? (
            <div className="aap__empty">
              No agents yet.<br />
              Create one on the left — it’ll show up here for you to scope and approve.
            </div>
          ) : (pending.length + active.length + inactive.length === 0) ? (
            <div className="aap__empty">No agents match “{search.trim()}”.</div>
          ) : (
            <>
              {pending.length > 0 && (
                <section className="aap__group">
                  <div className="aap__group-head">
                    <span className="aap__group-title aap__group-title--pending">● pending approval</span>
                    <span className="aap__group-count">{pending.length}</span>
                  </div>
                  {pending.map(renderGrant)}
                </section>
              )}
              {active.length > 0 && (
                <section className="aap__group">
                  <div className="aap__group-head">
                    <span className="aap__group-title aap__group-title--active">● active</span>
                    <span className="aap__group-count">{active.length}</span>
                  </div>
                  {active.map(renderGrant)}
                </section>
              )}
              {inactive.length > 0 && (
                <section className="aap__group">
                  <div className="aap__group-head aap__group-head--toggle" onClick={() => setShowInactive((s) => !s)}>
                    <span className="aap__group-title aap__group-title--inactive">{inactiveOpen ? '▾' : '▸'} inactive</span>
                    <span className="aap__group-count">{inactive.length}</span>
                  </div>
                  {inactiveOpen && inactive.map(renderGrant)}
                </section>
              )}
            </>
          )}
        </main>
      </div>

      {showHelp && (
        <div className="gads-neg-modal__overlay" onClick={() => setShowHelp(false)}>
          <div className="gads-neg-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 680, width: '92%', maxHeight: '85vh', overflowY: 'auto', textAlign: 'left' }}>
            <div className="gads-neg-modal__title">Connect an agent</div>
            <div className="gads-neg-modal__body" style={{ fontSize: 12.5, lineHeight: 1.55 }}>
              <p style={{ ...helpP, marginTop: 0 }}>An agent gets <b>scoped, single-client, time-boxed, owner-approved</b> access. It never receives your Google Ads credentials (the one exception is a client’s SiteGround SSH key, and only if you grant SSH access), and every write is a draft you approve here — nothing applies automatically. Revoke or let it expire anytime.</p>

              <div style={helpStep}>Recommended · create it here (Claude Code and other MCP clients)</div>
              <p style={helpP}>Hit <b>create agent</b> on the left. It appears as <b style={{ color: STATUS_TONE.pending }}>pending</b> with a one-time setup command: tick what it needs and <b>approve</b>, run the command in your terminal from the agent’s project folder, then start Claude Code there and give it the task. The agent never handles a code or secret itself, so its safety checks have nothing to block. Steps 1–3 below are only for agents that can’t work this way.</p>

              <div style={helpStep}>1 · Or: the agent requests access itself</div>
              <p style={helpP}>Give it the connect code + the manifest URL (or just hit <b>copy onboarding kit</b>). The agent GETs the manifest to discover every endpoint, then POSTs <code style={ic}>{'{ connectCode, label, purpose, idempotencyKey }'}</code> to <code style={ic}>requestAgentAccess</code> and receives a one-time secret. Some agents’ safety checks refuse this step as possible exfiltration — that’s what the recommended path above avoids.</p>
              <div style={kvRow}><span style={kvKey}>connect code</span><code style={codeInline}>{connectCode || '…'}</code><button type="button" className="gads-deep__act-btn" onClick={() => copy(connectCode)} disabled={!connectCode}>copy</button></div>
              <div style={kvRow}><span style={kvKey}>manifest URL</span><code style={codeInline}>{manifestUrl}</code><button type="button" className="gads-deep__act-btn" onClick={() => copy(manifestUrl)}>copy</button></div>
              <button type="button" className="gads-deep__neg-btn" style={{ marginTop: 4 }} onClick={() => copy(onboardingKit)} disabled={!connectCode}>📋 copy onboarding kit</button>

              <div style={helpStep}>2 · You scope + approve it</div>
              <p style={helpP}>It appears as <b style={{ color: STATUS_TONE.pending }}>pending</b>. Pick a <b>Google Ads scope</b> + <b>duration</b>, tick the <b>capabilities</b> it needs, then <b>approve</b>. Business data is scoped to one <b>bound client</b>; <b>Synergy hosting</b> is gated separately by an <b>allowed-hosting-IDs</b> allowlist (paste the hoid(s) it may touch), so a hosting-only agent needs no client.</p>

              <div style={helpStep}>3 · The agent connects</div>
              <p style={helpP}><b>Option A — MCP-native clients (e.g. Claude Code), recommended.</b> Add a remote MCP server; the tools are auto-discovered with full schemas — no guesswork.</p>
              <div style={codeWrap}><code style={codePre}>{mcpAddCmd}</code><button type="button" className="gads-deep__act-btn" onClick={() => copy(mcpAddCmd)}>copy</button></div>
              <p style={{ ...helpP, marginBottom: 4 }}>…or in <code style={ic}>.mcp.json</code>, reading the secret from an environment variable — that file is shared with the repo, so never put the raw secret in it:</p>
              <div style={codeWrap}><code style={codePre}>{mcpJson}</code><button type="button" className="gads-deep__act-btn" onClick={() => copy(mcpJson)}>copy</button></div>
              <p style={helpP}><b>Option B — raw HTTP.</b> Call the endpoints from the manifest with header <code style={ic}>Authorization: Bearer &lt;agentSecret&gt;</code>. POST <code style={ic}>{'{ describe: true }'}</code> to a tool for its full schema with examples.</p>
            </div>
            <div className="gads-neg-modal__actions">
              <button type="button" className="gads-neg-modal__apply" onClick={() => setShowHelp(false)}>Got it</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentAccessPanel;
